import { useCallback, useMemo } from 'react';
import { usePaginatedList } from '../../hooks';
import {
  ConversationQueryType,
  dispatch,
  getSelectedTimeRange,
  getWorkspaceId,
  updateQueryData,
  useAppSelector,
  useLazyGetConversationQueryTypesQuery,
} from '@zonofi/zono-money-store';
import { useNavigate } from 'react-router-dom';
import { SELLER_WEBAPP_ROUTES } from '../../constants';
import {
  getDateRangeFromTimeRange,
  getIsoWithMicroseconds,
} from '../../components';

export const useSemanticQueryList = () => {
  const navigate = useNavigate();
  const workspaceId = useAppSelector(getWorkspaceId);
  const selectedTimeRange = useAppSelector(getSelectedTimeRange);

  const [getConversationQueryTypesApi, conversationQueryTypesResponse] =
    useLazyGetConversationQueryTypesQuery();

  const fetchConversationQueryTypes = useCallback(
    (pageParams: { pageNo: number; pageSize: number }) => {
      const { startTime, endTime } =
        getDateRangeFromTimeRange(selectedTimeRange);

      getConversationQueryTypesApi({
        params: {
          sellerWorkspaceId: workspaceId,
          startTime: getIsoWithMicroseconds(startTime),
          endTime: getIsoWithMicroseconds(endTime),
          ...pageParams,
        },
      });
    },
    [selectedTimeRange]
  );

  const memoizedProducts = useMemo(() => {
    return conversationQueryTypesResponse?.data?.data || [];
  }, [conversationQueryTypesResponse?.data]);

  const {
    data: conversationQueryData,
    pageNo,
    lastItemRef,
    refresh,
    onScroll,
    isEndOfList,
  } = usePaginatedList<ConversationQueryType>({
    fetchDataApiCall: fetchConversationQueryTypes,
    apiResponseData: memoizedProducts,
    isLoading: conversationQueryTypesResponse.isLoading,
    uniqueKey: 'queryType',
    dependencies: [selectedTimeRange],
    isError: conversationQueryTypesResponse.isError,
    paginationMeta: undefined,
  });

  const onQueryCardPress = (query: ConversationQueryType) => {
    dispatch(updateQueryData(query));
    navigate(SELLER_WEBAPP_ROUTES.SEMANTIC_QUERY_DETAIL);
  };

  const totalQueries = useMemo(() => {
    return conversationQueryTypesResponse?.data?.pagination?.total;
  }, [conversationQueryTypesResponse?.data]);

  return {
    conversationQueryData,
    pageNo,
    lastItemRef,
    refresh,
    onScroll,
    isEndOfList,
    isLoading:
      pageNo === 1 &&
      (conversationQueryTypesResponse?.isUninitialized ||
        conversationQueryTypesResponse?.isFetching),
    onQueryCardPress,
    totalQueries,
  };
};
